/**
 * Multi-term substring filtering shared by the admin page search and the
 * command palette: every whitespace-separated term must match somewhere.
 * Matches rank in tiers — the query as one contiguous phrase in the item's
 * `title`, then as a phrase anywhere in `text`, then term-wise matches — so
 * "price" puts "Price configurations" above pages that only match through
 * their keywords, and "price c" puts it above pages that merely contain
 * both terms somewhere.
 *
 * The query is whatever the search field holds, which is null once its clear button has been
 * pressed — every caller here is an `fs-input`, and clearing one writes null rather than ''.
 */
export function filterByTerms<T>(
  items: T[],
  query: string | null | undefined,
  text: (item: T) => string,
  title?: (item: T) => string
): T[] {
  if (!query) return items;
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const phrase = terms.join(' ');
  const titleMatches: T[] = [];
  const phraseMatches: T[] = [];
  const termMatches: T[] = [];
  for (const item of items) {
    const haystack = text(item).toLowerCase();
    if (title && title(item).toLowerCase().includes(phrase)) titleMatches.push(item);
    else if (haystack.includes(phrase)) phraseMatches.push(item);
    else if (terms.every((term) => haystack.includes(term))) termMatches.push(item);
  }
  return [...titleMatches, ...phraseMatches, ...termMatches];
}

/** Enter-behavior for filtered lists: an exact title match wins, else the first item. */
export function pickFirstOrExact<T>(
  filtered: T[],
  query: string | null | undefined,
  text: (item: T) => string
): T | undefined {
  const wanted = (query ?? '').toLowerCase();
  const exact = filtered.find((item) => text(item).toLowerCase() === wanted);
  return exact ?? filtered[0];
}
